import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  notification,
  Tag,
  Typography,
  Input,
  Tooltip,
  DatePicker,
  Form,
} from 'antd';
import {
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchRfidList, useRemoveRFID, useToggleBlockUnblockRFID, useGenerateRFIDUsageReport } from './Hook';
import { useOperatorStore } from '../../store/useOperatorStore';
import AddRFIDModal from '../../components/Modals/AddRFIDModal';
import ViewRFIDModal from '../../components/Modals/ViewRFIDModal';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import { useAuthStore } from '../../store/useAuthStore';
import useDebounce from '../../utils/debounce';
import { formatKwFromWatts } from '../../utils/utils';
import WarningMessage from '../../components/WarningMessage';
import { getErrorMessage } from '../../utils/errorHandler';
const { Text, Title } = Typography;

export default function RFIDTable() {
  const { t } = useTranslation('rfid');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const [page, setPage] = useState(1);
  const [loadingRfidId, setLoadingRfidId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [rfidToView, setRfidToView] = useState(null);
  const [openCustomerModal, setOpenCustomerModal] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [rfidToBlockUnblock, setRfidToBlockUnblock] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [rfidForReport, setRfidForReport] = useState(null);
  const [dates, setDates] = useState(null);
  const [reportForm] = Form.useForm();
  const { mutate: generateRFIDUsageReport, isPending: isGeneratingReport } = useGenerateRFIDUsageReport();
  const { operatorId } = useOperatorStore();

  const rowsPerPage = 10;

  const { data, refetch, isFetching } = useFetchRfidList(
    operatorId,
    page,
    debouncedSearch,
    {
      enabled: operatorId !== null,
    }
  );

  const { mutate: toggleBlockUnblockRFID } = useToggleBlockUnblockRFID();
  const { mutate: removeRFID } = useRemoveRFID();

  const paginatedItems = data?.paginatedData || [];
  const totalCount = data?.totalCount || 0;

  useEffect(() => {
    if (operatorId) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]); // Add debouncedSearch to dependency array

  const handleViewRFID = (record) => {
    setRfidToView(record); // Remove restriction for global RFIDs
    setViewModalOpen(true);
  };

  const handleBlockUnblockRFID = (item) => {
    setRfidToBlockUnblock(item);
    if (item.status === 'Blocked') {
      setUnblockModalOpen(true);
    } else {
      setBlockModalOpen(true);
    }
  };

  const confirmBlockUnblockRFID = (block) => {
    const item = rfidToBlockUnblock;
    setLoadingRfidId(item._id);
    toggleBlockUnblockRFID(
      {
        id: item._id,
        operatorId,
        status: block, // true = block, false = unblock
      },
      {
        onSuccess: () => {
          const successMessageKey = block
            ? 'RFIDTable.SuccessBlock'
            : 'RFIDTable.SuccessUnblock';
          message.success(t(successMessageKey));
          refetch();
        },
        onError: (error) => {
          message.error(getErrorMessage(error, t('RFIDTable.ErrorBlockUnblock')));
        },
        onSettled: () => {
          setLoadingRfidId(null);
          setBlockModalOpen(false);
          setUnblockModalOpen(false);
        },
      });
  };

  const handleDeleteRFID = (item) => {
    setRfidToView(item);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRFID = () => {
    setLoadingRfidId(rfidToView._id);
    removeRFID(
      { id: rfidToView._id, operatorId },
      {
        onSuccess: () => {
          message.success(t('RFIDTable.SuccessDelete'));
          refetch();
        },
        onError: (error) => {
          message.error(getErrorMessage(error, t('RFIDTable.ErrorDelete')));
        },
        onSettled: () => {
          setLoadingRfidId(null);
          setDeleteModalOpen(false);
        },
      }
    );
  };

  const handleGenerateReport = async () => {
    try {
      const values = await reportForm.validateFields();
      if (!values.dateRange) return;

      const payload = {
        reportTemplate: 'RFID_USAGE_REPORT',
        operatorId,
        reportName:
          values.reportName ||
          t('RFIDTable.DefaultReportName', {
            rfid: rfidForReport?.RFID || '',
            date: dayjs().format('YYYY-MM-DD'),
          }),
        startTime: dayjs(values.dateRange[0]).format('YYYY-MM-DD'),
        endTime: dayjs(values.dateRange[1]).format('YYYY-MM-DD'),
        rfidId: rfidForReport?._id,
      };

      generateRFIDUsageReport(payload, {
        onSuccess: () => {
          setReportModalOpen(false);
          setRfidForReport(null);
          reportForm.resetFields();
          queryClient.invalidateQueries({ queryKey: ['CUSTOM_REPORT_LIST'] });
          notification.success({
            message: t('RFIDTable.ReportGeneratedSuccess'),
            description: (
              <div className="flex flex-col gap-2">
                <span>{t('RFIDTable.ReportAvailableInCustomReports')}</span>
                <Button
                  type="primary"
                  size="small"
                  className="w-fit"
                  onClick={() => {
                    navigate('/dashboard/reports', {
                      state: { activeMainTab: 'customReports' },
                    });
                    notification.destroy();
                  }}
                >
                  {t('RFIDTable.ViewCustomReports')}
                </Button>
              </div>
            ),
            duration: 10,
          });
        },
        onError: (error) => {
          message.error(getErrorMessage(error, t('RFIDTable.ReportGenerateError')));
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const renderStatusTag = (status) => {
    const statusColors = {
      Accepted: 'green',
      Blocked: 'red',
      Expired: 'gold',
      Invalid: 'orange',
      ConcurrentTx: 'blue',
    };

    return (
      <Tag color={statusColors[status] || 'default'} variant="outlined">
        {status === 'Accepted' ? t('RFIDTable.ACTIVE') : t('RFIDTable.BLOCKED')}
      </Tag>
    );
  };

  const getMonthlyUsageColorClass = (monthly, limit) => {
    if (limit > 0) {
      const ratio = monthly / limit;
      if (ratio >= 1) return 'text-red-500 font-semibold';
      if (ratio >= 0.75) return 'text-amber-500 font-medium';
      if (monthly > 0) return 'text-green-600';
      return 'text-gray-400';
    }
    if (monthly > 0) return 'text-green-600';
    return 'text-gray-400';
  };

  const renderUsageSoFar = (record) => {
    const monthly = Number(record.monthlyUsageSoFar ?? 0);
    const lifetime = Number(record.lifetimeUsageSoFar ?? 0);
    const limit = Number(record.limit ?? 0);
    const overLimit = limit > 0 && monthly >= limit;
    const nearLimit = limit > 0 && monthly >= limit * 0.75 && !overLimit;

    const monthlyDisplay =
      limit > 0
        ? `${formatKwFromWatts(monthly)} / ${formatKwFromWatts(limit)} kW`
        : `${formatKwFromWatts(monthly)} kW`;

    const monthlyColorClass = getMonthlyUsageColorClass(monthly, limit);
    const lifetimeColorClass = lifetime > 0 ? 'text-blue-600' : 'text-gray-400';

    return (
      <Tooltip
        title={
          overLimit
            ? t('RFIDTable.MonthlyLimitReached')
            : nearLimit
              ? t('RFIDTable.MonthlyLimitApproaching')
              : undefined
        }
      >
        <div className="flex flex-col items-center">
          <span className={monthlyColorClass}>{monthlyDisplay}</span>
          <span className="text-xs text-gray-400">{t('RFIDTable.MonthlyUsageSubtitle')}</span>
          <span className={`text-xs mt-1 ${lifetimeColorClass}`}>
            {formatKwFromWatts(lifetime)} kW · {t('RFIDTable.LifetimeUsageSubtitle')}
          </span>
        </div>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: t('RFIDTable.RFID'),
      dataIndex: 'RFID',
      key: 'RFID',
      align: 'center',
      render: (RFID, record) => (
        <Text
          className={`cursor-pointer ${record.isGlobal ? 'text-gray-400' : 'text-blue-500 hover:underline'
            }`}
          onClick={() => handleViewRFID(record)}
        >
          {RFID}
        </Text>
      ),
    },
    {
      title: t('RFIDTable.AssignedTo'),
      dataIndex: 'customer',
      align: 'center',
      key: 'assignedTo',
      render: (customer, record) => {
        const name =
          customer && (customer.fname || customer.lname)
            ? `${customer.fname || ''} ${customer.lname || ''}`.trim()
            : '';
        if (!name) return '-';

        const customerId = customer?._id ?? customer?.id ?? record?.customerId;
        const rowOperatorId = record?.operatorId ?? operatorId;

        if (!customerId || !rowOperatorId) return name;

        return (
          <Text
            className="cursor-pointer text-blue-500 hover:underline"
            onClick={() => setOpenCustomerModal({ id: customerId, operatorId: rowOperatorId })}
          >
            {name}
          </Text>
        );
      },
    },
    // {
    //   title: t('RFIDTable.CustomerEmail'),
    //   dataIndex: 'customer',
    //   key: 'customerEmail',
    //   render: (customer) => {
    //     return customer && customer.email ? customer.email : '-';
    //   },
    // },
    {
      title: t('RFIDTable.Status'),
      dataIndex: 'status',
      width: 80,
      key: 'status',
      align: 'center',
      render: (status) => renderStatusTag(status),
    },
    {
      title: t('RFIDTable.Balance'),
      dataIndex: 'limit',
      key: 'limit',
      align: 'center',
      render: (value) => {
        const displayValue =
          value === 0 || value == null
            ? t('RFIDTable.Unlimited')
            : `${formatKwFromWatts(value)} kW`;
        return (
          <div className="flex flex-col items-center">
            <span>{displayValue}</span>
            <span className="text-xs text-gray-400">
              {t('RFIDTable.LimitSubtitle')}
            </span>
          </div>
        );
      },
    },
    {
      title: t('RFIDTable.UsageSoFar'),
      key: 'usageSoFar',
      align: 'center',
      render: (_, record) => renderUsageSoFar(record),
    },
    {
      title: t('RFIDTable.Global'),
      dataIndex: 'isGlobal',
      key: 'isGlobal',
      align: 'center',
      render: (value) => (value ? t('RFIDTable.Yes') : t('RFIDTable.No')),
    },
    {
      title: t('RFIDTable.Actions'),
      key: 'actions',
      align: 'center',
      render: (_, item) => (
        <div className="flex gap-2">
          <div>
            <Tooltip title={t('RFIDTable.View')}>
              <Button
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewRFID(item);
                }}
              />
            </Tooltip>
          </div>
          <div>
            <Tooltip title={t('RFIDTable.Edit')}>
              <Button
                icon={<EditOutlined />}
                disabled={isReadOnlyUser}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewRFID(item);
                }}
              />
            </Tooltip>
          </div>
          <div>
            <Tooltip title={t('RFIDTable.RFIDUsageReport')}>
              <Button
                icon={<FileTextOutlined />}
                disabled={isReadOnlyUser}
                onClick={(e) => {
                  e.stopPropagation();
                  setRfidForReport(item);
                  reportForm.setFieldsValue({
                    reportName: t('RFIDTable.DefaultReportName', {
                      rfid: item.RFID,
                      date: dayjs().format('YYYY-MM-DD'),
                    }),
                  });
                  setReportModalOpen(true);
                }}
              />
            </Tooltip>
          </div>
          <div>
            <Tooltip title={item.status === 'Blocked' ? t('RFIDTable.Unblock') : t('RFIDTable.Block')}>
              <Button
                icon={
                  item.status === 'Blocked' ? <UnlockOutlined /> : <LockOutlined />
                }
                disabled={isReadOnlyUser}
                loading={loadingRfidId === item._id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBlockUnblockRFID(item);
                }}
              />
            </Tooltip>
          </div>
          <div>
            <Tooltip title={t('RFIDTable.Delete')}>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={isReadOnlyUser}
                loading={loadingRfidId === item._id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRFID(item);
                }}
              />
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  if (!operatorId) {
    return <WarningMessage />;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between mb-2 gap-4">
        <div className="flex items-center gap-2">
          <Input
            allowClear
            placeholder={t('RFIDTable.SearchPlaceholder')}
            prefix={<SearchOutlined />}
            onSearch={(value) => setSearchQuery(value)}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (page !== 1) setPage(1);
            }}
            style={{ width: 300 }}
          />
          <Tooltip title={t('RFIDTable.Refresh')}>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching}
            />
          </Tooltip>
        </div>
        <div className="flex items-center gap-4 justify-end">
          <Button
            type="primary"
            disabled={isReadOnlyUser}
            onClick={() => setAddModalOpen(true)}
          >
            {t('RFIDTable.AddRFID')}
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={paginatedItems}
        loading={isFetching}
        size='small'
        scroll={{ x: 1200, y: 60 * 8 }}
        pagination={{
          current: page,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: false,
          size: 'default',
          onChange: (page) => setPage(page),
          position: ['bottomCenter'],
        }}
        variant="outlined"
        rowKey={(record) => record._id}
      />
      {/* Add RFID Modal */}
      {addModalOpen && (
        <AddRFIDModal
          open={addModalOpen}
          setClose={() => setAddModalOpen(false)}
        />
      )}
      {/* View RFID Modal */}
      {viewModalOpen && rfidToView && (
        <ViewRFIDModal
          chargerData={rfidToView}
          open={viewModalOpen}
          setOpen={setViewModalOpen}
          refetchRFIDList={refetch}
        />
      )}
      {openCustomerModal?.id && (
        <CustomerDrawerView
          id={openCustomerModal.id}
          operatorId={openCustomerModal.operatorId}
          open={Boolean(openCustomerModal?.id)}
          onClose={() => setOpenCustomerModal(null)}
        />
      )}
      {/* Delete RFID Modal */}
      <Modal
        confirmLoading={loadingRfidId === rfidToView?._id}
        title={<Title level={4} className="mb-0!">{t('RFIDTable.DeleteRFIDTitle')}</Title>}
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={confirmDeleteRFID}
      >
        <p>{t('RFIDTable.DeleteRFIDMessage')}</p>
      </Modal>
      {/* Block RFID Modal */}
      <Modal
        confirmLoading={loadingRfidId === rfidToBlockUnblock?._id}
        title={<Title level={4} className="mb-0!">{t('RFIDTable.BlockRFIDTitle')}</Title>}
        open={blockModalOpen}
        onCancel={() => setBlockModalOpen(false)}
        onOk={() => confirmBlockUnblockRFID(true)}
      >
        <p>{t('RFIDTable.BlockRFIDMessage')}</p>
      </Modal>
      {/* Unblock RFID Modal */}
      <Modal
        confirmLoading={loadingRfidId === rfidToBlockUnblock?._id}
        title={<Title level={4} className="mb-0!">{t('RFIDTable.UnblockRFIDTitle')}</Title>}
        open={unblockModalOpen}
        onCancel={() => setUnblockModalOpen(false)}
        onOk={() => confirmBlockUnblockRFID(false)}
      >
        <p>{t('RFIDTable.UnblockRFIDMessage')}</p>
      </Modal>
      {/* Report Modal */}
      <Modal
        title={<Title level={4} className="mb-0!">{t('RFIDTable.RFIDUsageReport')}</Title>}
        open={reportModalOpen}
        onCancel={() => {
          setReportModalOpen(false);
          setRfidForReport(null);
          reportForm.resetFields();
        }}
        onOk={reportForm.submit}
        confirmLoading={isGeneratingReport}
        okText={t('RFIDTable.GenerateReport')}
        footer={
          <div className="flex justify-between items-center w-full">
            <Button
              type="link"
              className="p-0"
              onClick={() => {
                setReportModalOpen(false);
                setRfidForReport(null);
                reportForm.resetFields();
                navigate('/dashboard/reports', {
                  state: { activeMainTab: 'customReports' },
                });
              }}
            >
              {t('RFIDTable.ViewCustomReports')}
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setReportModalOpen(false);
                  setRfidForReport(null);
                  reportForm.resetFields();
                }}
              >
                {t('RFIDTable.cancel')}
              </Button>
              <Button
                type="primary"
                loading={isGeneratingReport}
                onClick={() => reportForm.submit()}
              >
                {t('RFIDTable.GenerateReport')}
              </Button>
            </div>
          </div>
        }
      >
        <Form form={reportForm} layout="vertical" onFinish={handleGenerateReport}>
          <Form.Item
            name="reportName"
            label={t('RFIDTable.ReportName')}
          >
            <Input placeholder={t('RFIDTable.ReportName')} />
          </Form.Item>
          <Form.Item
            name="dateRange"
            label={t('RFIDTable.SelectDateRange')}
            rules={[{ required: true, message: t('RFIDTable.DateRangeRequired') }]}
          >
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              onCalendarChange={(val) => setDates(val)}
              onOpenChange={(open) => {
                if (open) setDates(null);
              }}
              disabledDate={(current) => {
                if (!dates || dates.length === 0 || dates[1]) {
                  return false;
                }
                const tooLate = dates[0] && current.diff(dates[0], 'days') > 90;
                const tooEarly = dates[0] && dates[0].diff(current, 'days') > 90;
                return !!tooEarly || !!tooLate;
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
